/**
 * Dados ao vivo da operadora: retrato inicial + fluxo SSE (GUS-1104) com reconexão — Nexora Grid (frontend, US-402).
 *
 * Criado em: 16/10/2026 · Última modificação: 19/10/2026 · Versão: 1.2.0
 *
 * Guarda a última leitura de cada dispositivo/medida, uma tendência curta (últimos 60 pontos, para o mini-gráfico
 * da ISA-101), os dispositivos de dado velho e os alarmes novos. Reconecta sozinho (3 s, depois 10 s).
 * v1.1 (GUS-1503): a leitura traz o `municipio` (código do tópico) para as faixas por cidade.
 * v1.2 (P-027): o retrato traz o tipo de cada dispositivo no cadastro global (`tipos`).
 */
import { useEffect, useRef, useState } from "react";
import { api, lerFluxo } from "../api";

export type Leitura = { device_id: string; medida: string; valor: number; timestamp: string; unidade: string; municipio?: string; ativos: string[] };
export type AlarmeNovo = { alarme_id: string; device_id: string; tipo: string; severidade: string; descricao: string; recebido_em: string };
export type EstadoTempoReal = {
  leituras: Map<string, Leitura>; tendencias: Map<string, number[]>; velhos: Set<string>;
  alarmesNovos: AlarmeNovo[]; conectado: boolean; atualizadoEm: Date | null; versao: number;
  /** P-027: tipo de cada dispositivo no cadastro global (reservatorio, poco, ponto_pressao…). */
  tipos: Map<string, string>;
};

const PONTOS = 60;
export const chave = (device: string, medida: string) => `${device}|${medida}`;

export function useTempoReal(empresa: string | null): EstadoTempoReal {
  const leituras = useRef(new Map<string, Leitura>());
  const tendencias = useRef(new Map<string, number[]>());
  const [velhos, setVelhos] = useState<Set<string>>(new Set());
  const [tipos, setTipos] = useState<Map<string, string>>(new Map());
  const [alarmesNovos, setAlarmesNovos] = useState<AlarmeNovo[]>([]);
  const [conectado, setConectado] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    if (!empresa) return;
    let vivo = true;
    const controle = new AbortController();
    const aplicar = (lista: Leitura[]) => {
      for (const l of lista) {
        const k = chave(l.device_id, l.medida);
        const anterior = leituras.current.get(k);
        if (anterior?.timestamp === l.timestamp) continue;
        leituras.current.set(k, l);
        const serie = tendencias.current.get(k) ?? [];
        serie.push(l.valor);
        if (serie.length > PONTOS) serie.shift();
        tendencias.current.set(k, serie);
      }
      setAtualizadoEm(new Date());
      setVersao((v) => v + 1);
    };
    (async () => {
      let espera = 3000;
      while (vivo) {
        try {
          const retrato = await api<{ leituras: Leitura[]; dados_velhos: string[]; tipos?: Record<string, string> }>(`/v1/tempo-real/ultimas?empresa=${empresa}`,
            { sinal: controle.signal });
          aplicar(retrato.leituras);
          setVelhos(new Set(retrato.dados_velhos));
          setTipos(new Map(Object.entries(retrato.tipos ?? {})));
          setConectado(true);
          espera = 3000;
          await lerFluxo(`/v1/tempo-real?empresa=${empresa}`, (e) => {
            const dados = e.dados as Record<string, unknown>;
            if (e.evento === "leituras") aplicar(dados.leituras as Leitura[]);
            else if (e.evento === "estado") setVelhos(new Set(dados.dados_velhos as string[]));
            else if (e.evento === "alarmes") setAlarmesNovos((a) => [...(dados.alarmes as AlarmeNovo[]), ...a].slice(0, 100));
          }, controle.signal);
        } catch {
          if (!vivo) return;
        }
        setConectado(false);
        await new Promise((r) => setTimeout(r, espera));
        espera = 10000;
      }
    })();
    return () => { vivo = false; controle.abort(); };
  }, [empresa]);

  return { leituras: leituras.current, tendencias: tendencias.current, velhos, alarmesNovos, conectado, atualizadoEm, versao, tipos };
}
